import type { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useAuthStore } from "@/stores/authStore";

let cachedUser: User | null | undefined = undefined;
let userInFlight: Promise<User | null> | null = null;

function fetchAuthUserOnce(): Promise<User | null> {
  if (userInFlight !== null) return userInFlight;
  userInFlight = (async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      cachedUser = user ?? null;
      return cachedUser;
    } catch (e) {
      cachedUser = null;
      throw e;
    } finally {
      userInFlight = null;
    }
  })();
  return userInFlight;
}

/**
 * Returns current user id. Uses auth store when available; otherwise uses
 * the same deduplicated fetch as getCurrentUser() so only one network request is made.
 */
export async function getCurrentUserId(): Promise<string | null> {
  const fromStore = useAuthStore.getState().userId;
  if (fromStore) return fromStore;
  if (cachedUser !== undefined) return cachedUser?.id ?? null;
  const user = await fetchAuthUserOnce();
  return user?.id ?? null;
}

export function clearCurrentUserIdCache(): void {
  cachedUser = undefined;
  userInFlight = null;
}

/**
 * Returns current user. Shares a single deduplicated supabase.auth.getUser() with
 * getCurrentUserId() so concurrent callers (e.g. tasks screen, ticket notes) do not
 * trigger multiple auth/v1/user requests.
 */
export const getCurrentUser = async (): Promise<{
  success: boolean;
  data?: User | null;
  error?: unknown;
}> => {
  try {
    const fromStore = useAuthStore.getState();
    if (fromStore.user) return { success: true, data: fromStore.user };
    if (cachedUser !== undefined) return { success: true, data: cachedUser };
    const user = await fetchAuthUserOnce();
    return { success: true, data: user };
  } catch (error) {
    console.error("Error fetching current user:", error);
    return { success: false, error };
  }
};

export const getSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    return { success: true, data: session };
  } catch (error) {
    console.error("Error fetching session:", error);
    return { success: false, error };
  }
};

export const verifyCurrentPassword = async (email: string, password: string) => {
  try {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error("Error verifying password:", error);
    return { success: false, error };
  }
};

export const updatePassword = async (newPassword: string) => {
  try {
    // Ensure we have a valid session before updating
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { success: false, error: new Error('No active session') };
    }

    // Set the session explicitly to avoid stale token issues
    await supabase.auth.setSession({
      access_token: session.access_token,
      refresh_token: session.refresh_token,
    });

    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      console.error("updateUser error:", error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error: any) {
    console.error("Error updating password:", error);
    return { success: false, error };
  }
};

export const resetPasswordForEmail = async (email: string, redirectToOrigin: string) => {
  try {
    // Use the provided origin so it works in preview/staging/custom domains.
    // Note: this URL must be allowed in Supabase Auth > URL Configuration.
    const origin = (() => {
      try {
        return new URL(redirectToOrigin).origin;
      } catch {
        return redirectToOrigin.replace(/\/+$/, '');
      }
    })();
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${origin}/reset-password`,
    });

    if (error) {
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error("Error resetting password:", error);
    return { success: false, error };
  }
};

export const signInWithPassword = async (email: string, password: string) => {
  try {
    const { error } = await supabase.auth.signInWithPassword({
      email: email.toLowerCase(),
      password,
    });

    if (error) {
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error("Error signing in with password:", error);
    return { success: false, error };
  }
};

export const signInWithOtp = async (email: string, redirectToOrigin: string) => {
  try {
    // Use the provided origin so it works in preview/staging/custom domains.
    // Note: this URL must be allowed in Supabase Auth > URL Configuration.
    const origin = (() => {
      try {
        return new URL(redirectToOrigin).origin;
      } catch {
        return redirectToOrigin.replace(/\/+$/, '');
      }
    })();
    
    const { error } = await supabase.auth.signInWithOtp({
      email: email.toLowerCase(),
      options: {
        emailRedirectTo: `${origin}/auth/callback`,
      },
    });

    if (error) {
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error("Error signing in with OTP:", error);
    return { success: false, error };
  }
};

export const validateCustomerEmail = async (email: string) => {
  try {
    const { data, error } = await supabase.functions.invoke('validate-customer-email', {
      body: { email: email.toLowerCase() }
    });

    if (error) {
      return { success: false, error, data: null };
    }

    return { success: true, data };
  } catch (error) {
    console.error("Error validating customer email:", error);
    return { success: false, error };
  }
};

export const onAuthStateChange = (callback: (event: string, session: any) => void | Promise<void>) => {
  return supabase.auth.onAuthStateChange(callback);
};

export const signOut = async () => {
  try {
    clearCurrentUserIdCache();
    const { error } = await supabase.auth.signOut();
    if (error) {
      return { success: false, error };
    }
    return { success: true };
  } catch (error) {
    console.error("Error signing out:", error);
    return { success: false, error };
  }
};

export const getUser = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return { success: true, data: user };
  } catch (error) {
    console.error("Error fetching user:", error);
    return { success: false, error };
  }
};

export const verifyRecoveryToken = async (token: string) => {
  try {
    const { data, error } = await supabase.auth.verifyOtp({
      token_hash: token,
      type: 'recovery',
    });

    if (error) {
      return { success: false, error, data: null };
    }

    return { success: true, data: data?.session || null };
  } catch (error) {
    console.error("Error verifying recovery token:", error);
    return { success: false, error, data: null };
  }
};

export const updateEmail = async (newEmail: string, redirectToOrigin: string) => {
  try {
    // Use the provided origin so it works in preview/staging/custom domains.
    const origin = (() => {
      try {
        return new URL(redirectToOrigin).origin;
      } catch {
        return redirectToOrigin.replace(/\/+$/, '');
      }
    })();

    const { error } = await supabase.auth.updateUser({
      email: newEmail.toLowerCase(),
    }, {
      emailRedirectTo: `${origin}/app/profile`,
    });

    if (error) {
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error("Error updating email:", error);
    return { success: false, error };
  }
};
